import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { UserService } from '../../services/user.service';
import { User } from '../../models/user.model';
import { Subscription } from 'rxjs';
import { SessionService } from '../../services/session.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Loader } from '../../classes/loader';
import { NotifyOrConfirmService } from '../../services/notify-or-confirm.service';
import { TranslateService } from '@ngx-translate/core';
import { ConfigService } from '../../services/config.service';
import { LoggerService } from '../../services/logger.service';
import { Router } from '@angular/router';
import { LanguageService } from '../../services/language.service';

@Component({
	selector: 'cfe-terms-and-conditions-modal',
	templateUrl: './terms-and-conditions-modal.component.html',
	styleUrls: ['./terms-and-conditions-modal.component.scss']
})
export class TermsAndConditionsModalComponent implements AfterViewInit, OnDestroy, OnInit {
	public modal: NgbModalRef;

	@ViewChild('termsAndConditionsModal', { static: true }) modalElementRef: ElementRef;

	private user: User;
	private userSub: Subscription;
	private loader = new Loader();
	private dateOfEnactment: Date;
	private currentLanguageCodeSub: Subscription;
	private currentLanguageCode: string;
	private removeTabKeyListener: () => void;

	deadlineReached = false;
	infoText: string;
	form: FormGroup;

	constructor(
		private modalService: NgbModal,
		private ngZone: NgZone,
		private userService: UserService,
		private sessionService: SessionService,
		private formBuilder: FormBuilder,
		private notification: NotifyOrConfirmService,
		private translateService: TranslateService,
		private configService: ConfigService,
		private loggerService: LoggerService,
		private router: Router,
		private renderer: Renderer2,
		private languageService: LanguageService
	) {}

	ngOnInit(): void {
		this.createForm();
		this.dateOfEnactment = new Date(this.configService.config.termsAndConditions.dateOfEnactment);
		this.currentLanguageCodeSub = this.languageService.language$.subscribe((code) => {
			this.currentLanguageCode = code;
		});
		const deadline = this.configService.config.termsAndConditions.dateOfEnactment;
		this.deadlineReached = new Date(deadline).getTime() <= new Date(Date.now()).getTime();
		this.infoText = this.deadlineReached
			? 'terms.modal.changedTermsAndConditionsTextDeadlineReached'
			: 'terms.modal.changedTermsAndConditionsText';
	}

	createForm() {
		this.form = this.formBuilder.group({
			acceptTermsCheckbox: [null, [Validators.pattern('true')]]
		});
	}

	ngAfterViewInit() {
		this.userSub = this.sessionService.currentUserObservable.subscribe((user) => {
			if (user) {
				this.user = user;
				this.checkShouldModalOpen();
			}
		});
	}

	checkShouldModalOpen() {
		if (this.modal || this.router.url === '/') {
			return;
		}

		const dateOfEnactment = new Date(this.configService.config.termsAndConditions.dateOfEnactment).getTime();
		const dateOfAnnouncement = new Date(this.configService.config.termsAndConditions.dateOfAnnouncement).getTime();
		const acceptedTermsAndConditionsAt = new Date(this.user.acceptedTermsAndConditions).getTime();
		const currentDate = Date.now();

		if (isNaN(dateOfEnactment) || isNaN(dateOfAnnouncement)) {
			this.loggerService.error('Modal could not be displayed. Date format in the config is wrong!');
			return;
		}

		if (dateOfAnnouncement <= currentDate && (!this.user.acceptedTermsAndConditions || acceptedTermsAndConditionsAt < dateOfAnnouncement)) {
			this.showModal();
		}
	}

	private stopTabKeyEvent() {
		this.removeTabKeyListener = this.renderer.listen('document', 'keydown', (event) => {
			if (event.key === 'Tab') {
				event.preventDefault();

				let focusedElementId: string = (event.target as Element).id;

				const laterButton = document.getElementById('later-button');
				const okButton = document.getElementById('ok-button');
				const termsAndConditionsCheckBox = document.getElementById('acceptTermsOfConditions-checkbox');
				const termsAndConditionsLabel = document.getElementById('terms-and-conditions-label');

				if (focusedElementId === 'acceptTermsOfConditions-checkbox') {
						termsAndConditionsLabel.focus();
				} else if (focusedElementId === 'terms-and-conditions-label') {
					if (!laterButton['disabled']) {
						laterButton.focus();
					} else if (!okButton['disabled']) {
						okButton.focus();
					} else {
						termsAndConditionsCheckBox.focus();
					}
				} else if (focusedElementId === 'later-button') {
					if (!okButton['disabled']) {
						okButton.focus();
					} else {
						termsAndConditionsCheckBox.focus();
					}
				} else {
					termsAndConditionsCheckBox.focus();
				}
			}
		});
	}

	public showModal() {
		this.stopTabKeyEvent();
		this.ngZone.run(() => {
			this.modal = this.modalService.open(this.modalElementRef, {
				size: 'lg',
				windowClass: 'modalview-confirm d-flex align-items-center terms-modal-width',
				backdrop: 'static',
				keyboard: false
			});
		});
	}

	private closeModal() {
		this.removeTabKeyListener();
		this.ngOnDestroy();
		this.modal.close();
		this.modal = null;
	}

	onSubmit() {
		const data = { acceptedTermsAndConditions: new Date(Date.now()) };
		this.loader.loadUntilComplete(this.userService.update(this.user.uid, data)).subscribe(
			(response: User) => {
				this.closeModal();
				this.sessionService.currentUser = response;
			},
			(error) => {
				this.closeModal();
				this.loggerService.error('Error updating user, Error: ', error);
				this.notification.showNotification(
					this.translateService.instant('terms.error.title'),
					this.translateService.instant('terms.error.errorSavingDataMessage')
				);
			}
		);
	}

	ngOnDestroy(): void {
		this.renderer.destroy();
		this.userSub.unsubscribe();
		this.currentLanguageCodeSub.unsubscribe();
	}
}
